import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ActivePromo {
  id: number;
  code: string;
  valueType: string;
  value: string;
  startsAt: string | null;
  endsAt: string | null;
  targetType: string;
}

interface PromoState {
  promo: ActivePromo | null;
  isLoading: boolean;
  error: string | null;
  expiresAt: Date | null;
}

// Fallback promo data (from Shopify - CS25 active promo)
const FALLBACK_PROMO: ActivePromo = {
  id: 2113756266760,
  code: "CS25",
  valueType: "percentage",
  value: "-25",
  startsAt: "2026-02-01T00:00:00+01:00",
  endsAt: "2026-02-08T23:59:59+01:00",
  targetType: "line_item",
};

export function useActivePromo(): PromoState {
  const [state, setState] = useState<PromoState>({
    promo: FALLBACK_PROMO,
    isLoading: true,
    error: null,
    expiresAt: new Date(FALLBACK_PROMO.endsAt!),
  });
  const [hasShownToast, setHasShownToast] = useState(false);

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('shopify-promos');
        
        if (error) {
          console.error('Error fetching promos:', error);
          // Keep fallback data
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        if (data?.success && data.promos?.length > 0) {
          // Find the main promo (CS25 or first active one)
          const mainPromo = data.promos.find((p: ActivePromo) => p.code.toUpperCase() === "CS25") || data.promos[0];
          const expiresAt = mainPromo.endsAt ? new Date(mainPromo.endsAt) : null;
          
          setState({
            promo: mainPromo,
            isLoading: false,
            error: null,
            expiresAt,
          });
        } else {
          setState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (err) {
        console.error('Error:', err);
        setState(prev => ({ ...prev, isLoading: false, error: 'Failed to fetch promos' }));
      }
    };

    fetchPromos();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchPromos, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Check for expiration toast (< 24h remaining)
  useEffect(() => {
    if (!state.expiresAt || hasShownToast) return;

    const checkExpiration = () => {
      const now = new Date();
      const timeLeft = state.expiresAt!.getTime() - now.getTime();
      const hoursLeft = timeLeft / (1000 * 60 * 60);
      
      // Show toast if less than 24 hours remaining
      if (hoursLeft > 0 && hoursLeft <= 24 && !hasShownToast) {
        const hours = Math.floor(hoursLeft);
        const minutes = Math.floor((hoursLeft - hours) * 60);
        
        toast.warning(`⏰ Only ${hours}h${minutes > 0 ? ` ${minutes}min` : ''} left!`, {
          description: `Code ${state.promo?.code} expires soon. Get ${Math.abs(parseFloat(state.promo?.value || "0"))}% off now!`,
          duration: 8000,
          action: {
            label: "View products",
            onClick: () => {
              const productsSection = document.getElementById('products');
              productsSection?.scrollIntoView({ behavior: 'smooth' });
            },
          },
        });
        
        setHasShownToast(true);
      }
    };

    // Check immediately
    checkExpiration();
    
    // Check every minute
    const interval = setInterval(checkExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [state.expiresAt, state.promo, hasShownToast]);

  return state;
}
